import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { Button, ChangeEvent, Checkbox, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownItem, Image, ImageType, Label, Layout, Panel, RowModeControlType, ScreenStack, SelectionMode, SelectionType, SlideoutDecorator, Snackbar, Switch, Tab, TableCell, TableColumn, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, Textbox } from "@mcleod/components";
import { SortSelector } from "@mcleod/components/src/components/table/SortSelector";
import { Alignment, Api, CurrencyUtil, DisplayType, DOMUtil, getThemeColor, HorizontalAlignment, LogManager, Model, ModelRow, NumberUtil, StringUtil } from "@mcleod/core";
import { SendLoadTender } from "@mcleod/datafusion";
import { getDispatchControlValue, LMECompanySettings } from "@mcleod/general";
import { ImageName } from "@mcleod/images";
import { CarrierAssignment } from "@mcleod/powerbroker";
import { AutogenLayoutCarrierSelection } from "./autogen/AutogenLayoutCarrierSelection";
import { CarrierAssignmentPrompt } from "./CarrierAssignmentPrompt";
import { CarrierSelectionExpand } from "./CarrierSelectionExpand";
import { CarrierSelectionRateChange } from "./CarrierSelectionRateChange";
import { OrderTabView } from "./OrderTabView";
import { Project44Profile } from "./Project44Profile";
import { SMC3Profile } from "./SMC3Profile";

const log = LogManager.getLogger("lme.dispatch.CarrierSelection");

interface ValidCarrierResult { isValid: boolean, reason_code?: string, reason?: string }

export enum BfgStatusCode { SelectedForDispatch = "SFD", Include = "INCL", Exclude = "EXCL", Chosen = "CHOS"}
export enum BfgTypeCode { BlanketCost = "BCST", Default = "DFLT" }
export enum SupportingVendor { P44 = "P44", SMC3 = "SMC3" }
export enum ContractManagementControl { SMC3 = "SLA", P44 = "P44" }
export enum CarrierAssignmentReason { LowestSell = "C", LowestBuy = "S" }

const QUOTE_STATUS_APPROVED = "A";
const QUOTE_ID_PREFIX = "Q";

export class CarrierSelection extends AutogenLayoutCarrierSelection {
    private _orderRow: ModelRow;
    private isQuoteOrder: boolean;
    private _defaultRow: ModelRow;
    private p44ConfigurationProfile: ModelRow;
    private smc3ConfigurationProfile: ModelRow;
    private _chosenCarrier: ModelRow;
    private _selectedForDispatchCarrier: ModelRow;
    private _p44Record: ModelRow;
    private _smc3Record: ModelRow;
    private _tariffHasSmc3: boolean = false;
    private _tariffHasP44: boolean = false;
    private checkboxSelectAll: Checkbox;
    private allUnchecked = true;

    public doAfterChoosingCarrier: () => Promise<void>;

    private p44RatingModes: DropdownItem[] = [
        new DropdownItem("LTL", "LTL"),
        new DropdownItem("VOL", "VOL"),
        new DropdownItem("LTV", "LTL and VOL")
    ];

    private p44DirectionOverrides: DropdownItem[] = [
        new DropdownItem("SHIPPER", "Shipper"),
        new DropdownItem("CONSIGNEE", "Consignee"),
        new DropdownItem("THIRD_PARTY", "Third Party")
    ];

    private paymentTerms: DropdownItem[] = [
        new DropdownItem("PREPAID", "Prepaid"),
        new DropdownItem("COLLECT", "Collect"),
        new DropdownItem("THIRD_PARTY", "Third Party")
    ];

    private smc3RatingModes: DropdownItem[] = [
        new DropdownItem("CON", "Contract"),
        new DropdownItem("DYN", "Dynamic"),
        new DropdownItem("VOM", "Volume"),
        new DropdownItem("CDP", "Contract and Dynamic"),
        new DropdownItem("CVP", "Contract and Volume"),
        new DropdownItem("DVP", "Dynamic and Volume"),
        new DropdownItem("CDV", "Contract, Dynamic, and Volume")
    ];

    private smc3PayerOverrides: DropdownItem[] = [
        new DropdownItem("SHIPPER", "Shipper"),
        new DropdownItem("CONSIGNEE", "Consignee"),
        new DropdownItem("THIRD_PARTY", "Third Party")
    ];

    public get orderRow(): ModelRow {
        return this._orderRow;
    }

    public set orderRow(value: ModelRow) {
        this._orderRow = value;
        this.isQuoteOrder = this.isQuoteOrderId(value.get("id"));
        this.getRates();
        this.refreshHeaderInfo();
    }

    public get chosenCarrier(): ModelRow {
        return this._chosenCarrier;
    }

    public set chosenCarrier(value: ModelRow) {
        if (value != null && value.get("bfg_uid") != this.chosenCarrier?.get("bfg_uid")) {
            this._chosenCarrier = value;
            this.doAfterChoosingCarrier();
        } else {
            this._chosenCarrier = value;
        }
    }

    public get p44Record(): ModelRow {
        return this._p44Record;
    }

    public set p44Record(value: ModelRow) {
        this._p44Record = value;
    }

    public get smc3Record(): ModelRow {
        return this._smc3Record;
    }

    public set smc3Record(value: ModelRow) {
        this._smc3Record = value;
    }

    public set tariffHasSmc3(value: boolean) {
        this._tariffHasSmc3 = value;
    }

    public get tariffHasSmc3(): boolean {
        return this._tariffHasSmc3;
    }

    public set tariffHasP44(value: boolean) {
        this._tariffHasP44 = value;
    }

    public get tariffHasP44(): boolean {
        return this._tariffHasP44;
    }

    public get selectedForDispatchCarrier(): ModelRow {
        return this._selectedForDispatchCarrier;
    }

    public set selectedForDispatchCarrier(value: ModelRow) {
        this._selectedForDispatchCarrier = value;
        if (this.isQuoteOrder) {
            this.buttonFindCarrierRates.visible = !value;
        }
    }

    public get defaultRow(): ModelRow {
        return this._defaultRow;
    }

    public set defaultRow(value: ModelRow) {
        this._defaultRow = value;
    }

    override async onLoad() {
        this.styleTab(this.tabSpotQuote);
        this.styleTab(this.tabModeAndTerms);
        this.tableBFGs.addRowCollapseListener((collapseEvent) => {
            if (this.tableBFGs.rows.every(row => !row.expanded)) {
                this.switchExpandAll.checked = false;
            }
        });
        this.tableBFGs.addRowExpandListener((expandEvent) => {
            if (this.tableBFGs.rows.every(row => row.expanded)) {
                this.switchExpandAll.checked = true;
            }
        });
        this.p44Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "P44" });
        this.smc3Record = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "SLA" });
        this.tabModeAndTerms.expanded = true;
        this.tableBFGs.columns.forEach( (column : TableColumn) => {
            const sortSelector = column.headingCell.findComponentByType(SortSelector);
            if (sortSelector) {
                sortSelector.addClickListener(() => {
                    this.tableBFGs.rows.forEach((row) => {
                        this.switchExpandAll.checked = false;
                        row.expanded = false;
                    });
                });
            }
        });
    }

    public async getRates() {
        const rateRows = [];
        const errorRows = [];
        const allRows: ModelRow[] = this.mainDataSource.data;

        this.sortBFGData(allRows, rateRows, errorRows);
        this.tableBFGs.allowFilter = false;
        this.tableBFGs.data = rateRows;
        this.tableBFGs.data.forEach(row => {
            if (row.get("bfg_status_code") == "CHOS") this.chosenCarrier = row;
            if (row.get("bfg_status_code") == "SFD") this.selectedForDispatchCarrier = row;
        });
        if (!!this.chosenCarrier || !!this.selectedForDispatchCarrier) this.buttonChangeMarkups.visible = false;
        this.tableBFGs.displayComponentData(null, rateRows, 0);
        this.tableErrors.data = errorRows;
        this.tableErrors.displayComponentData(null, errorRows, 0);
        this.tabRates.caption = "Rates" + ` (${rateRows.length})`;
        this.tabErrors.caption = "Errors" + ` (${errorRows.length})`;
        if (errorRows.length > 0) {
            this.tabErrors.headingLabel.color = "error.lightest";
        }

        const shouldExpandAll = getDispatchControlValue("carrier_select_expand_all") == "Y";
        this.expandAll(shouldExpandAll);

        const orderId = this.orderRow?.get("id") ?? this.defaultRow?.get("order_id");
        if (orderId !== null) {
            const response = await Api.post("lme/powerbroker/inspect_customer_tariff", { order_id: orderId });
            const data = response.data[0];
            this.tariffHasSmc3 = data?.tariff_has_smc3;
            this.tariffHasP44 = data?.tariff_has_p44;
            this.manageRatingControlDisplayTab();
        }

        if (this.isQuoteOrder) {
            const hasSelectColumn = this.configureSelectColumn();
            if (hasSelectColumn) {
                this.syncSelectAllCheckbox();
            }
        } else if (this.isQuoteOrder === false) {
            this.tableBFGs.removeColumn("columnHeaderSelected");
        }

    }

    manageRatingControlDisplayTab() {
        const showP44: boolean = !!(this.p44Record?.getBoolean("is_active") && this.tariffHasP44);
        if (showP44) {
            this.textboxRatingMode.items = this.p44RatingModes;
            this.textboxRatingMode.visible = showP44;
            this.textboxRatingMode.setSelectedItemFromValue(this.defaultRow?.get("rating_mode"));
            this.textboxRatingMode.tooltip = "Select value to choose type of rate request for P44";
            this.textboxP44DirectionOverride.items = this.p44DirectionOverrides;
            this.textboxP44DirectionOverride.visible = showP44;
            this.textboxP44DirectionOverride.setSelectedItemFromValue(this.defaultRow?.get("p44_direction_override"));
            this.textboxPaymentTerms.visible = showP44;
            this.textboxPaymentTerms.items = this.paymentTerms;
            this.textboxPaymentTerms.setSelectedItemFromValue(this.defaultRow?.get("payment_terms"));
            this.textboxPaymentTerms.tooltip = "Select value to override payment terms for P44";
            this.buttonP44Settings.visible = showP44;
        }

        const showSmc3: boolean = !!(this.smc3Record?.getBoolean("is_active") && this.tariffHasSmc3);
        if (showSmc3) {
            this.textboxSmc3RatingMode.items = this.smc3RatingModes;
            this.textboxSmc3RatingMode.visible = showSmc3;
            this.textboxSmc3RatingMode.setSelectedItemFromValue(this.defaultRow?.get("smc3_rating_mode"));
            this.textboxSmc3RatingMode.tooltip = "Select value to choose type of rate request for SMC3";
            this.textboxSmc3PayerOverride.items = this.smc3PayerOverrides;
            this.textboxSmc3PayerOverride.visible = showSmc3;
            this.textboxSmc3PayerOverride.setSelectedItemFromValue(this.defaultRow?.get("smc3_payer_override"));
            this.textboxSmc3PaymentTerms.items = this.paymentTerms;
            this.textboxSmc3PaymentTerms.visible = showSmc3;
            this.textboxSmc3PaymentTerms.setSelectedItemFromValue(this.defaultRow?.get("smc3_payment_terms"));
            this.textboxSmc3PaymentTerms.tooltip = "Select value to override payment terms for SMC3";
            this.buttonSmc3Settings.visible = showSmc3;
            this.buttonSmc3Settings.caption = "SMC3 Settings";
        }
        this.tabsetRatingControls.visible = showSmc3 || showP44;
    }

    tableBFGsOnSelect() {
        if (this.buttonFindCarrierRates.busy === true)
            return;
        this.tableBFGs.allRows.forEach((tableRow: TableRow) => {
            const modelRow = tableRow.data as ModelRow;
            const statusCode = modelRow.get("bfg_status_code")
            if (tableRow.selected === true && !StringUtil.equalsIgnoreCase(statusCode, "INCL")) {
                modelRow.set("bfg_status_code", "INCL");
                modelRow.post();
            }
            if (tableRow.selected === false && !StringUtil.equalsIgnoreCase(statusCode, "EXCL")) {
                modelRow.set("bfg_status_code", "EXCL");
                modelRow.post();
            }
        });
    }

    isIncludedCarrierQuote(bfgRow: ModelRow) {
        return bfgRow.get("bfg_status_code") !== "EXCL";
    }

    sortBFGData(allRows: ModelRow[], rateRows, errorRows) {
        allRows.forEach(row => {
            if (row.data["bfg_type_code"] == "QERR" || row.data["bfg_type_code"] == "QPHB")
                errorRows.push(row);
            else if (row.data["bfg_type_code"] == "DFLT") {
                this.defaultRow = row;
            } else rateRows.push(row);
        });
        const rateWeights = {
            "CHOS": 0, // Chosen
            "SFD": 0, // Selected for Dispatch
            "Manual": 1 // Spot Quote
        }
        rateRows = rateRows.sort((a, b) => {
            const aWeight = a.getString("carrier_name").toUpperCase().includes("[INCOMPLETE SETUP]") ? 3 : rateWeights[a.get("bfg_status_code")] ?? rateWeights[a.get("rating_source")] ?? 2;
            const bWeight = b.getString("carrier_name").toUpperCase().includes("[INCOMPLETE SETUP]") ? 3 : rateWeights[b.get("bfg_status_code")] ?? rateWeights[b.get("rating_source")] ?? 2;
            if (aWeight == bWeight) {
                const selectionSort = getDispatchControlValue("carrier_selection_sort");
                const aSell = a.get("total_charge_amount").amount;
                const bSell = b.get("total_charge_amount").amount;
                switch (selectionSort) {
                    case "LM":
                        return (NumberUtil.getFloat(a.get("total_margin_amount")) - NumberUtil.getFloat(b.get("total_margin_amount"))
                            || aSell-bSell); // Lowest Margin
                    case "LMU":
                        return  (NumberUtil.getFloat(a.get("total_markup_amount")) -  NumberUtil.getFloat(b.get("total_markup_amount"))
                            || aSell - bSell); // Lowest Markup
                    case "LS": // Lowest Sell
                        if (a.get("transit_days") && b.get("transit_days"))
                            return (aSell - bSell || a.get("transit_days") - b.get("transit_days"));
                        else
                            return aSell - bSell; // Lowest Sell
                    case "LB":
                        return (a.get("total_revenue_amount").amount - b.get("total_revenue_amount").amount || aSell- bSell); // Lowest Buy
                    case "HM":
                        return  (NumberUtil.getFloat(b.get("total_margin_amount")) - NumberUtil.getFloat(a.get("total_margin_amount"))
                            || aSell - bSell); // Highest Margin
                    case "HMU":
                        return  (NumberUtil.getFloat(b.get("total_markup_amount")) -  NumberUtil.getFloat(a.get("total_markup_amount"))
                            || aSell - bSell); // Highest Markup
                    case "HS":
                        if (a.get("transit_days") && b.get("transit_days"))
                            return (bSell - aSell || a.get("transit_days") - b.get("transit_days"));// Highest Sell
                        else
                            return bSell - aSell;
                    case "HB":
                        return (b.get("total_revenue_amount").amount - a.get("total_revenue_amount").amount || aSell - bSell); // Highest Buy
                    case "FT":
                        if (a.get("transit_days") && b.get("transit_days"))
                            return (a.get("transit_days") - b.get("transit_days") || aSell-bSell); // Fastest Transit
                }
            }
            return aWeight - bWeight;
        });
    }

    expandAll(shouldBeExpanded: boolean) {
        this.switchExpandAll.checked = shouldBeExpanded;
        this.tableBFGs.setRowsExpanded(shouldBeExpanded, null, () => DOMUtil.scrollElementIntoView(this.tableBFGs.rows[0]?._element));
    }

    switchExpandAllOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const switchExpand = event.target as Switch;
            this.expandAll(switchExpand.checked);
        }
    }

    sourceBrltlBillingFreightGroupBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.ADD) {
            if (this.tableBFGs.data.length > 0 && this.defaultRow?.hasChanged()) {
                const originalRatingMode = this.defaultRow.originalData["rating_mode"];
                const originalP44DirectionMode = this.defaultRow.originalData["p44_direction_override"];
                const originalPaymentTerms = this.defaultRow.originalData["payment_terms"];
                const originalSmc3RatingMode = this.defaultRow.originalData["smc3_rating_mode"];
                const originalSmc3PaymentTerms = this.defaultRow.originalData["smc3_payment_terms"];
                const originalSmc3PayerMode = this.defaultRow.originalData["smc3_payer_override"];
                CommonDialogs.showYesNo("Updating Rating Controls may affect rates. Would you like to re-rate?", "Re-rate Order", {
                    yesButtonCaption: "Yes", noButtonCaption: "No"
                }).then((response) => {
                    if (response)
                        this.buttonFindCarrierRates.clicked();
                    else {
                        this.updateDefaultRow(originalRatingMode, originalP44DirectionMode, originalPaymentTerms,
                            originalSmc3RatingMode, originalSmc3PaymentTerms, originalSmc3PayerMode);
                        this.textboxRatingMode.displayData(this.defaultRow, null, 0);
                        this.textboxP44DirectionOverride.displayData(this.defaultRow, null, 0);
                        this.textboxPaymentTerms.displayData(this.defaultRow, null, 0);
                        this.textboxSmc3RatingMode.displayData(this.defaultRow, null, 0);
                        this.textboxSmc3PaymentTerms.displayData(this.defaultRow, null, 0);
                        this.textboxSmc3PayerOverride.displayData(this.defaultRow, null, 0);
                    }
                });
            }
        }
    }

    sourceBrltlBillingFreightGroupAfterExecution(event: DataSourceExecutionEvent) {
        this.getRates();
    }

    async updateDefaultRow(ratingMode: string, p44DirectionOverride: string, paymentTerms: string, smc3RatingMode: string, smc3PaymentTerms: string,
        smc3PayerOverride: string
    ) {
        this.defaultRow.set("rating_mode", ratingMode);
        this.defaultRow.set("p44_direction_override", p44DirectionOverride);
        this.defaultRow.set("payment_terms", paymentTerms);
        this.defaultRow.set("smc3_rating_mode", smc3RatingMode);
        this.defaultRow.set("smc3_payment_terms", smc3PaymentTerms == undefined ? null : smc3PaymentTerms);
        this.defaultRow.set("smc3_payer_override", smc3PayerOverride);
        if (this.defaultRow.hasChanged()) {
            await this.defaultRow.post();
        }
    }

    async findCarrierRatesOnClick(event: ClickEvent) {
        const rerateButton = event.target as Button;
        rerateButton.busy = true;
        this.tableBFGs.rows.forEach((row) => row.findComponentById("buttonChooseCarrier").enabled = false)
        this.updateDefaultRow(
            this.textboxRatingMode.selectedItem?.value ?? null,
            this.textboxP44DirectionOverride.selectedItem?.value ?? null,
            this.textboxPaymentTerms.selectedItem?.value ?? null,
            this.textboxSmc3RatingMode.selectedItem?.value ?? null,
            this.textboxSmc3PaymentTerms.selectedItem?.value ?? null,
            this.textboxSmc3PayerOverride.selectedItem?.value ?? null
        );
        await CarrierSelection.findCarrierRates(this.activeRow.get("order_id"), this.p44ConfigurationProfile, this.smc3ConfigurationProfile,
            () => this.mainDataSource.search({ order_id: this.orderRow.get("id") }));
        rerateButton.busy = false;
        this.expandAll(this.switchExpandAll.checked);
        if (this.isQuoteOrder) {
            this.syncSelectAllCheckbox();
        }
    }

    static async findCarrierRates(orderId: String, p44ConfigurationProfile?: ModelRow, smc3ConfigurationProfile?: ModelRow, afterRerate?: () => Promise<any>, snackbarTarget: Panel = ScreenStack.getSnackbarTarget()) {
        const progressSnackbar: Snackbar = Snackbar.showDownloadSnackbar("Carrier Rerating", "Finding Carrier Rates...", {
            persist: true,
            targetPanel: snackbarTarget
        });
        const result = (await Api.search("lme/powerbroker/find-carrier-rates", {
            order_id: orderId, p44_configuration: p44ConfigurationProfile, smc3_configuration: smc3ConfigurationProfile
        })).data[0];
        progressSnackbar.dismiss();
        const requestStatus = result["request_status"];
        const responseMessage = result["response_message"];
        progressSnackbar.dismiss();
        if (responseMessage != null) {
            Snackbar.showSnackbar(responseMessage, { customMaxWidth: 750, customBackground: "error.lightest", persist: true });
        }
        if (requestStatus === "SUCCESS" && result["new_chosen_bfg_uid"] != null) {
            const updatedRates = (await Api.search("lme/dispatch/brltl-billing-freight-group", { new_chosen_bfg_uid: result["new_chosen_bfg_uid"] })).data;
            const [currentRate, newRate] = updatedRates.map((rate: Partial<any>) => new ModelRow("lme/dispatch/brltl-billing-freight-group", false, rate)).sort((a, b) => a.get("bfg_status_code") == "CHOS" ? -1 : 1);
            CarrierSelectionRateChange.showSlideout(currentRate, newRate, afterRerate);
        }
        else
            await afterRerate?.();

        return true;
    }

    async buttonP44SettingsOnClick(event: ClickEvent) {
        const project44ProfileLayout = Layout.getLayout("lme/dispatch/Project44Profile") as Project44Profile;
        new SlideoutDecorator({
            layout: project44ProfileLayout,
            layoutLoadListeners: async () => {
                if (this.p44ConfigurationProfile) {
                    project44ProfileLayout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [this.p44ConfigurationProfile]);
                } else {
                    // first check if there is a default p44 profile for the customer
                    let p44Profile = await Model.searchSingleRecord("lme/ar/customer", { id: this.orderRow.get("customer_id") });
                    if (p44Profile.data?.p44_profile_id == null) {
                        p44Profile = await Model.searchSingleRecord("lme/powerbroker/contract-management-control", { id: "P44" });
                    }
                    const p44DefaultControlId = p44Profile.data?.p44_profile_id;
                    if (p44DefaultControlId) {
                        project44ProfileLayout.mainDataSource.search({ id: p44DefaultControlId });
                    }
                }
                project44ProfileLayout.textboxId.printable = true;
                project44ProfileLayout.textboxDescription.printable = true;
                project44ProfileLayout.switchCarrierConfiguration.visible = false;
                project44ProfileLayout.textboxRatingMode.printable = true;
                project44ProfileLayout.textboxDirectionOverride.printable = true;
                project44ProfileLayout.textboxPaymentTerms.printable = true;
            },
            fillVerticalSpace: true,
            transitionOptions: { direction: Alignment.RIGHT },
            title: "Project44 Settings",
            addlComponents: new Button({
                id: "submitP44Btn",
                caption: "Rerate",
                onClick: () => this.p44SettingsOnSave(project44ProfileLayout),
                backgroundColor: "primary",
                color: "primary.reverse",
                borderWidth: 0,
                minWidth: 128,
                rowBreak: false
            })
        })
    }

    async buttonSmc3SettingsOnClick(event: ClickEvent) {
        const smc3ProfileLayout = Layout.getLayout("lme/dispatch/SMC3Profile") as SMC3Profile;
        new SlideoutDecorator({
            layout: smc3ProfileLayout,
            layoutLoadListeners: async () => {
                if (this.smc3ConfigurationProfile) {
                    smc3ProfileLayout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [this.smc3ConfigurationProfile]);
                } else {
                    // first check if there is a default smc3 profile for the customer
                    let smc3Profile: ModelRow = await Model.searchSingleRecord("lme/ar/customer", { id: this.orderRow.get("customer_id") });
                    if (smc3Profile.data?.smc3_profile_id == null) {
                        smc3Profile = this.smc3Record;
                    }
                    const smc3DefaultControlId = smc3Profile.data?.smc3_profile_id;
                    if (smc3DefaultControlId) {
                        smc3ProfileLayout.mainDataSource.search({ id: smc3DefaultControlId });
                    }
                }
                smc3ProfileLayout.textboxId.printable = true;
                smc3ProfileLayout.textboxDescription.printable = true;
                smc3ProfileLayout.textboxRatingMode.printable = true;
                smc3ProfileLayout.textboxPayerTerms.printable = true;
                smc3ProfileLayout.textboxPaymentTerms.printable = true;
            },
            fillVerticalSpace: true,
            width: 900,
            transitionOptions: { direction: Alignment.RIGHT },
            title: "SMC3 Settings",
            overlayProps: { greyedBackground: true, closeOnClickOff: false},
            addlComponents: new Button({
                id: "submitSMC3Btn",
                caption: "Rerate",
                onClick: () => this.smc3SettingsOnSave(smc3ProfileLayout),
                backgroundColor: "primary",
                color: "primary.reverse",
                minWidth: 128,
                borderWidth: 0,
                rowBreak: false
            })
        })
    }

    p44SettingsOnSave(p44Layout: Project44Profile) {
        this.p44ConfigurationProfile = p44Layout.mainDataSource.getDataboundValues();
        p44Layout.slideOut().then(() => {
            this.buttonFindCarrierRates.clicked();
        });
    }

    smc3SettingsOnSave(smc3Layout: SMC3Profile) {
        this.smc3ConfigurationProfile = smc3Layout.mainDataSource.getDataboundValues();
        const quoteTimeOut = this.smc3ConfigurationProfile.get("quote_timeout");
        if ((quoteTimeOut == undefined || quoteTimeOut == null) || (quoteTimeOut < 1 || quoteTimeOut > 30)) {
            CommonDialogs.showError("Quote timeout must be between 1 and 30 seconds");
            return;
        }
        const scacBatchSize = this.smc3ConfigurationProfile.get("scacs_batch_size");
        if ((scacBatchSize == undefined || scacBatchSize == null) || (scacBatchSize < 1 || scacBatchSize > 35)) {
            CommonDialogs.showError("SCAC Batch Size must be between 1 and 35");
            return;
        }
        smc3Layout.slideOut().then(() => {
            this.buttonFindCarrierRates.clicked();
        });
    }

    updateDefaultBFG(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const textbox = event.target as Textbox;
            this.defaultRow.set(textbox.field, textbox.selectedItem?.value ?? null);
        }
    }

    tableBFGsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const labelBlanketRate = tableRow.findComponentById("labelBlanketRate") as Label;
        labelBlanketRate.visible = tableRow.data.get("bfg_type_code", "") === "BCST";

        if (this.isQuoteOrder && this.isIncludedCarrierQuote(tableRow.data)) {
            const includeCheckbox: Checkbox = tableRow.findComponentById("checkboxInclude") as Checkbox;
            if (includeCheckbox) {
                includeCheckbox.checked = true;
            }
        }

        const labelTransit = tableRow.findComponentById("labelTransit") as Label;
        labelTransit.style.textAlign = "left";

        let textbox: Textbox = tableRow.findComponentById("textboxTotalMarkupAmount") as Textbox;
        if (parseFloat(tableRow.data.get("total_markup_amount")) < 0)
            textbox.color = getThemeColor("error");
        else
            textbox.color = getThemeColor("default")

        textbox = tableRow.findComponentById("textboxTotalMarginAmount") as Textbox;
        if (parseFloat(tableRow.data.get("total_margin_amount")) < 0)
            textbox.color = getThemeColor("error");
        else
            textbox.color = getThemeColor("default")

        // Carrier Qualification logic
        const qualification = tableRow.data.get("qualification_status", null);
        const inadequateTransit: boolean = tableRow.data.get("inadequate_transit_time", false);
        const isExpired: boolean = tableRow.data?.get("is_expired", false);
        const canMoveHazmat: boolean = tableRow.data?.get("can_move_hazmat", false);
        const labelCarrierInformation = tableRow.findComponentById("labelCarrierInformation") as Label;
        labelCarrierInformation.style.textAlign = "left";
        CommonTooltips.setTooltipContentLayout(labelCarrierInformation, tableRow.data, "lme/dispatch/brltl/CarrierInformationQuickInfo");
        const panelImage = tableRow.findComponentById("panelImage") as Panel;
        CommonTooltips.setTooltipContentLayout(panelImage, tableRow.data, "lme/dispatch/brltl/CarrierInformationQuickInfo");
        const imageBytes = tableRow.data.get("image_bytes", null);
        if (imageBytes != null) {
            if (panelImage.getComponentCount() == 0) {
                const carrierImage = new Image({
                    id: "carrierImg",
                    imageType: ImageType.IMG,
                    preserveAspectRatio: true,
                    height: 50,
                    width: 100
                });
                carrierImage.imageBytes = imageBytes;
                panelImage.add(carrierImage);
            }
        }
        const carrierQualPanel = tableRow.findComponentById("panelCarrierQualification") as Panel;
        let qualificationImage: Image;
        const buttonChooseCarrier = tableRow.findComponentById("buttonChooseCarrier") as Button;
        if (StringUtil.equalsIgnoreCase("Disqualified", qualification) || isExpired || !canMoveHazmat) {
            qualificationImage = new Image({ name: ImageName.circleError, color: "error", width: "20", height: "20" });
            if (tableRow.data.getString("carrier_name", "").toUpperCase().includes("[INCOMPLETE SETUP]")) {
                qualificationImage.tooltip = `A carrier record with a SCAC of "${tableRow.data.get("carrier_scac")}" has not been set up in the system.`;
            } else {
                CommonTooltips.setTooltipContentLayout(qualificationImage, tableRow.data, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
            }
            buttonChooseCarrier.enabled = false;
        } else if (StringUtil.equalsIgnoreCase("Warning", qualification) || tableRow.data.get("bfg_comment") != null) {
            qualificationImage = new Image({
                name: ImageName.triangleWarning,
                color: "warning",
                width: "24",
                height: "24"
            });
            if (StringUtil.equalsIgnoreCase("Warning", qualification)) {
                CommonTooltips.setTooltipContentLayout(qualificationImage, tableRow.data, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
            } else {
                qualificationImage.tooltipDynamicContent = () => {
                    return tableRow.data.get("bfg_comment");
                };
            }
        } else if (inadequateTransit === true) {
            qualificationImage = new Image({
                name: ImageName.circleQuestion,
                color: "caution",
                width: "24",
                height: "24",
                tooltip: "Carrier might not make the Transit Time"
            });
        }
        if (qualificationImage && carrierQualPanel.getComponentCount() == 0) {
            carrierQualPanel.add(qualificationImage);
        }

        // Quote Expiration Logic
        if (tableRow.data.get("expiration_date") != null) {
            if (tableRow.data.get("is_expired") === true) {
                const expirationActiveImage = tableRow.findComponentById("imageExpirationActive") as Image;
                expirationActiveImage.visible = true;
                expirationActiveImage.tooltip = "This carrier rate expired on " + tableRow.data.get("expiration_date");
            }
            else {
                const expirationSetImage = tableRow.findComponentById("imageExpirationSet") as Image;
                expirationSetImage.visible = true;
                expirationSetImage.tooltip = "This carrier rate expires on " + tableRow.data.get("expiration_date");
            }
        }

        buttonChooseCarrier.caption = this.getChooseCaption(tableRow.data);
        buttonChooseCarrier.disabledTooltip = (() => {
            if (!this.isQuoteOrder && this.chosenCarrier != null) {
                return "A carrier has already been selected for this order.";
            } else if (this.isQuoteOrder) {
                if (!!this.selectedForDispatchCarrier) {
                    return "A carrier has already been selected for this quote.";
                } else if (this.orderRow.get("quote_status") != "A") {
                    return "Quote status must be Approved prior to selecting a carrier quote.";
                }
                else if (!this.isValidQuote()) {
                    return "This quote is expired.";
                }
            }
            return null;
        })();
        buttonChooseCarrier.enabled = buttonChooseCarrier.disabledTooltip === null;

        // highlight the chosen carrier if there is one
        if (tableRow.data.get("bfg_uid") == (this.chosenCarrier?.get("bfg_uid") ?? this.selectedForDispatchCarrier?.get("bfg_uid"))) {
            tableRow.backgroundColor = "success.lightest";
            if (this.selectedForDispatchCarrier) {
                buttonChooseCarrier.caption = "Tender";
                buttonChooseCarrier.enabled = true;
                if (LMECompanySettings.getSingleton().isBrokerageLtl() && this.isQuoteOrder) {
                    Api.post("lme/dispatch/brltl/get-created-order-id-for-quote", { quote_id: this.activeRow.get("order_id") }).then(result => {
                        if (result?.data[0].order_id != null) {
                            buttonChooseCarrier.caption = result.data[0].order_id;
                            buttonChooseCarrier.tooltip = "Open Order";
                        }
                    });
                }
            }
        }
    }

    getChooseCaption(bfgRow: ModelRow) {
        const quoteCaption = "Choose & Create Order";
        const p44Caption = "P44 Dispatch";
        const smc3Caption = "SMC3 Dispatch";
        const defaultCaption = "Choose";

        if (this.isQuoteOrder)
            return quoteCaption;

        if (this.p44Record?.getBoolean("is_active") && this.p44ConfigurationProfile != null) {
            if (("P" === this.p44ConfigurationProfile.get("send_disp_request") &&
                "P44" === bfgRow.get("calc_type_code")) || ("A" === this.p44ConfigurationProfile.get("send_disp_request")))
                return p44Caption;
            else if ("P44" === bfgRow.get("calc_type_code"))
                return defaultCaption;
        }
        return bfgRow.getBoolean("dispatch_to_p44") ? p44Caption : bfgRow.getBoolean("dispatch_to_smc3") ? smc3Caption : defaultCaption;
    }

    public tableBFGsOnRowExpand(expansionEvent: TableRowExpansionEvent) {
        const tableRow: TableRow = expansionEvent.target as TableRow;
        const expandPanel = expansionEvent.expandComponentParent as Panel;
        const layoutExpanded: CarrierSelectionExpand = expandPanel.findComponentById("layoutCarrierSelectionExpand") as CarrierSelectionExpand;
        if (expansionEvent.isExpanding) {
            if (this.chosenCarrier || (this.isQuoteOrder && this.selectedForDispatchCarrier)) {
                layoutExpanded.tableRevenueDetails.rowModeControlType = RowModeControlType.AUTO;
            }
            layoutExpanded.chosenCarrier = this.chosenCarrier;
            layoutExpanded.setParentTableRow(tableRow);
            const data = tableRow.data?.get("revenue_details")?.map((row: Partial<any>) => new ModelRow("lme/dispatch/revenue-detail", false, row)).sort(CarrierSelection.sortRevenueDetails) ?? [];
            layoutExpanded.mainDataSource?.setRowsAndMode(DataSourceMode.NONE, data);
            layoutExpanded.tableRevenueDetails.columnHeadingsVisible = false;
            this.panelTableBfg.rows[0].style.height = "100%"; // pretty nasty hack to make the vertical scroll bar behave
        }
    }

    public static sortRevenueDetails = (a: ModelRow, b: ModelRow) => {
        enum revDetailWeights {
            BASE,
            DEF,
            DEFW,
            ASWT,
            ASWE,
            MAXB,
            MBAS,
            OBCH,
            OVCH,
            OGCH,
            OVBA,
            OVBR,
            OBBA,
            OBBR,
            ODIS,
            OGBA,
            OGBR,
            BFIX,
            DNAG,
            DISC,
            FUEL
        }

        const aWeight = revDetailWeights[`${a.get("gla_code")}`] ?? 100;
        const bWeight = revDetailWeights[`${b.get("gla_code")}`] ?? 100;
        return aWeight - bWeight;
    };

    async buttonChooseCarrierOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(button);
        button.busy = true;
        try {
            await tableRow.data.post();
            const carrierValidation = await this.validateCarrierForDispatch(tableRow);
            if (carrierValidation.isValid) {
                if (this.isQuoteOrder) {
                    await this.selectCarrierForDispatch(tableRow);
                } else {
                    await this.assignCarrier(carrierValidation, tableRow);
                    this.sendLoadTender();
                }
            }
        } catch (error) {
            log.error(error);
            CommonDialogs.showServerError(error as any);
        }
        button.busy = false;
    }

    async assignCarrier(carrierValidation: ValidCarrierResult, tableRow: TableRow) {
        return Api.post("lme/dispatch/carrier-dispatch", {
            bfg_uid: tableRow.data.get("bfg_uid"),
            reason_code: carrierValidation.reason_code,
            reason: carrierValidation.reason,
            send_disp_request: "P44" === tableRow.data.get("calc_type_code") ? this.p44ConfigurationProfile?.get("send_disp_request") :
                "SMC3" === tableRow.data.get("calc_type_code") ? this.smc3ConfigurationProfile?.get("send_dispatch_request") : null
        }).then(async (response) => {
            if (response.data[0]?.["message"]) {
                Snackbar.showSnackbar(response.data[0]["message"], { persist: true });
            }
            this.selectedForDispatchCarrier = null;
            this.chosenCarrier = tableRow.data;
            this.mainDataSource.search({ order_id: this.activeRow.get("order_id") });
        });
    }

    async selectCarrierForDispatch(tableRow: TableRow) {
        if (this.selectedForDispatchCarrier || this.chosenCarrier) {
            const quoteId = this.activeRow.get("order_id");
            const isBrokerageLTL = LMECompanySettings.getSingleton().isBrokerageLtl();
            const result = await this.orderIdForQuote(quoteId);
            const orderId = result?.order_id;
            if (isBrokerageLTL && !StringUtil.isEmptyString(orderId)) {
                this.navigateToOrderTabView(orderId, false, true)
            } else {
                CommonDialogs.showMessage("Order " + orderId + " has already been created from this quote.", "Order Created")
            }
            return;
        }

        const oldStatusCode = tableRow.data.get("bfg_status_code", "");
        try {
            tableRow.data.set("bfg_status_code", "SFD");
            await this.orderRow.post();
            await tableRow.data.post();
            await this.doAfterChoosingCarrier();
            const quoteId = tableRow.data.get("order_id");
            const result = await this.convertQuoteToOrder(quoteId);
            const orderId = result?.id;
            this.navigateToOrderTabView(orderId, true, false);
        } catch (error) {
            log.error(error);
            CommonDialogs.showServerError(error as any);
            tableRow.data.set("bfg_status_code", oldStatusCode);
            await tableRow.data.post();
        }
    }

    private async validateCarrierForDispatch(tableRow: TableRow) {
        let result: ValidCarrierResult = { isValid: true };
        const selectedCarrier = tableRow.data as ModelRow;
        const response = await Api.post("lme/dispatch/carrier-dispatch-validation", {
            bfg_uid: selectedCarrier.get("bfg_uid"),
            is_quote: this.isQuoteOrder
        });
        const data = response.data[0];
        const selectedForDispatch = data?.selected_for_dispatch;
        const warnings: String[] = data?.warnings;
        if (!selectedForDispatch) {
            const reasonDesc = this.generateCarrierAssignPromptMessage(selectedCarrier);
            if (!!reasonDesc) {
                result = await this.awaitCarrierResponsePrompt(reasonDesc);
            }
        }
        if (result.isValid && warnings.length > 0) {
            result = await this.awaitCarrierQualPrompt();
        }
        return result;
    }

    private generateCarrierAssignPromptMessage(selectedCarrier: ModelRow) {
        switch (getDispatchControlValue("carrier_assignment_prompt")) {
            case "C": { // Lowest Cost
                return this.getLowestCarrierMessage(selectedCarrier, "total_revenue_amount");
            }
            case "S": { // Lowest Charge
                return this.getLowestCarrierMessage(selectedCarrier, "total_charge_amount");
            }
            default:
                return null;
        }
    }

    private getLowestCarrierMessage(selectedCarrier: ModelRow, field: string) {
        const lowestCarrier = this.tableBFGs.data.sort((a, b) => a.get(field).amount - b.get(field).amount)[0];
        if (lowestCarrier.get("bfg_uid") == selectedCarrier.get("bfg_uid")) return null; // Has qualification warnings but does not fall into this case
        return `${selectedCarrier.get("carrier_name")} rate of ${selectedCarrier.getCurrency(field)} was selected over the cheaper ${lowestCarrier.get("carrier_name")} rate of ${lowestCarrier.getCurrency(field)}.`;
    }

    private async awaitCarrierResponsePrompt(reasonDesc: string) {
        const layout = Layout.getLayout("lme/dispatch/CarrierAssignmentPrompt") as CarrierAssignmentPrompt;
        layout.addLayoutLoadListener(() => {
            layout.textboxReasonCode.addBeforeLookupModelSearchListener((event) => {
                event.filter.code_type = "CAREX"
            })
            layout.textboxReason.text = reasonDesc;
        })
        const clickedYes = await CommonDialogs.showYesNo(layout, "Assignment Reason", {
            yesButtonCaption: "OK", noButtonCaption: "Cancel"
        });
        return {
            isValid: clickedYes,
            reason_code: layout.textboxReasonCode.text,
            reason: layout.textboxReason.text
        };
    }

    private async awaitCarrierQualPrompt() {
        return {
            isValid: await CommonDialogs.showYesNo("Carrier has a warning status based on the carrier qualification profile. Continue?", "Carrier Assignment", {
                yesButtonCaption: "OK", noButtonCaption: "Cancel"
            })
        };
    }

    sendLoadTender() {
        const carrierId = this.chosenCarrier.get("payee_id");
        const movementId = this.chosenCarrier.get("movement_id");
        let carrierStatus = "false";
        let nextPurpose = "00";

        CarrierAssignment.checkCarrierForDataFusion(carrierId, movementId).then(result => {
            carrierStatus = result.data ? result.data[0].is_datafusion_carrier : result.is_datafusion_carrier;
            nextPurpose = result.data ? result.data[0].next_purpose : result.next_purpose;
            if (carrierStatus == "auto") {
                CarrierAssignment.sendAutoTender(carrierId, movementId).then(result => {
                    const tenderSent = result.data[0]["tendersent"];
                    log.debug("Result: " + tenderSent);
                    if (tenderSent) {
                        const notes = result.data[0].text;
                        Snackbar.showSnackbar("Result: " + notes + ".");
                    } else {
                        Snackbar.showWarningSnackbar("Failed to send outbound tender to DataFusion carrier " + carrierId + ": " + result.data[0].text);
                    }
                });
            } else if (carrierStatus == "manual") {
                const slideoutProps = {
                    width: 1200, height: window.innerHeight, backgroundColor: "defaultBackground", top: 30, padding: 0,
                    borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
                };
                Layout.getLayout("lme/datafusion/SendLoadTender", {
                    ...slideoutProps,
                    width: 1000
                }).addLayoutLoadListener(event => {
                    const layout = event.target as SendLoadTender;
                    layout.buttonCancel.addClickListener(() => layout.slideOut());
                    layout.buttonSendLater.visible = false;
                    layout.search({
                        carrier: carrierId,
                        move_id: movementId,
                        is_cancel: "false",
                        is_auto: "false"
                    }).then(result => {
                        if (result?.modelRows?.length > 0) {
                            layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
                            layout.slideIn({ direction: Alignment.RIGHT }, true, {
                                closeOnClickOff: false,
                                greyedBackground: true
                            });
                        }
                    });
                });
            }
        });
    }

    buttonAddSpotQuoteOnClick(event: ClickEvent) {
        const button = event.target as Button;
        if (this.tabSpotQuote.validateSimple()) {
            button.busy = true;
            Api.post("lme/dispatch/carrier-spot-quote", {
                order_id: this.activeRow.get("order_id"),
                transit_days: this.textboxTransitDays.text,
                payee_id: this.textboxCarrier.getFirstLookupModelData()?.get("id"),
                base_charge: !this.textboxBaseAmount.isEmpty() ? CurrencyUtil.removeFormatting(this.textboxBaseAmount.text) : null,
                source: this.textboxSource.selectedItem?.displayValue,
                ref_num: this.textboxReference.getDataValue(),
                fuel_charge: !this.textboxFuelAmount.isEmpty() ? CurrencyUtil.removeFormatting(this.textboxFuelAmount.text) : null,
                accessorial_charge: !this.textboxAccessorialAmount.isEmpty() ? CurrencyUtil.removeFormatting(this.textboxAccessorialAmount.text) : null
            }).then(async result => {
                if (result.data[0]?.error) {
                    Snackbar.showWarningSnackbar(result.data[0]?.error);
                } else {
                    this.tabSpotQuote.forEveryChildComponent(component => component instanceof Textbox ? component.clear() : null);
                    this.tabSpotQuote.expandCollapseTab(false, false);
                    await this.mainDataSource.search({ order_id: this.activeRow.get("order_id") });
                    this.expandAll(this.switchExpandAll.checked);
                }
            }).finally(() => {
                button.busy = false;
            })
        }
    }

    async buttonChangeMarkupsOnClick(event: ClickEvent) {
        const textboxMarkupProps = { placeholder: "Enter markup amount (example: 25)", displayType: DisplayType.DECIMAL };
        try {
            const result = await CommonDialogs.showInputDialog("Enter a new markup amount", "Change Markups", textboxMarkupProps);

            const markupAmount = parseFloat(result);
            if (StringUtil.isEmptyString(result) || isNaN(markupAmount) || markupAmount < 0) {
                const message = "Change Markups only accepts markup amounts of 0 or greater, no markups changed.";
                CommonDialogs.showError(message);
                return;
            }

            this.switchExpandAll.checked = true;
            await this.tableBFGs.setRowsExpanded(true, null, () => DOMUtil.scrollElementIntoView(this.tableBFGs.rows[0]?._element));
            const tableRows: TableRow[] = this.tableBFGs.rows;

            for (const tableRow of tableRows) {
                await tableRow.populateDOM();
                tableRow.expanded = true;
                let expandComp = await tableRow.getExpansionComponentLoaded();
                if (expandComp == null) {
                    await tableRow.populateDOM();
                    tableRow.expanded = true;
                    expandComp = await tableRow.getExpansionComponentLoaded();
                }
                const panel = expandComp as Panel;
                if (panel) {
                    const layoutExpanded = panel.findComponentById("layoutCarrierSelectionExpand") as CarrierSelectionExpand;
                    for (const row of layoutExpanded.tableRevenueDetails.rows) {
                        if (row.data.get("markup_amount") != null) {
                            row.data.set("markup_amount", result);
                            layoutExpanded.performCalculationsOnRatingData(row.data, "markup_amount");
                        }
                    }
                }
            }

        } catch (error) {
            const message = "An error occurred while changing the markup amount or processing the markup amount.";
            CommonDialogs.showError(message);
        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "default.light", fontSize: "medium", fontBold: true });
        }
    }

    refreshHeaderInfo() {
        const modelPath = this.isQuoteOrder ? "lme/dispatch/brltl/quote-freight-group-item" : "lme/dispatch/brltl-order-freight-group-item";
        Model.search(modelPath, {
            lme_order_id: this.orderRow.get("id"),
            fgp_uid: this.orderRow.get("fgp_uid")
        }).then(result => {
            const firstFreightClass = result.modelRows[0]?.get("nmfc_class_code");
            const totalWeight = result.modelRows.reduce((currentTotal, row) => {
                const weight = row.get("weight") ?? 0;
                const uom = row.get("weight_uom_type_code") ?? "LBS";
                const weightInLbs = uom === "KGS" ? weight * 2.20462 : weight;
                return currentTotal + weightInLbs;
            }, 0);
            const freightClass = result.modelRows?.every(row => row.get("nmfc_class_code") == firstFreightClass) ? firstFreightClass : "Multiple";
            const orderAndStopInfo = new ModelRow("lme/dispatch/orders", false, {
                stop_origin_sched_arrive_early: (this.orderRow as ModelRow).get("stop_origin.sched_arrive_early"),
                stop_origin_city_name: (this.orderRow as ModelRow).get("stop_origin.city_name"),
                stop_origin_state: (this.orderRow as ModelRow).get("stop_origin.state"),
                stop_consignee_city_name: (this.orderRow as ModelRow).get("stop_consignee.city_name"),
                stop_consignee_state: (this.orderRow as ModelRow).get("stop_consignee.state"),
                total_weight: NumberUtil.formatDecimal(totalWeight ?? 0, "#,###.#") + " lbs",
                freight_class: freightClass
            })
            this.panelOverview.displayData(orderAndStopInfo, null, 0);
        });
    }

    isValidQuote() {
        const currentDate: number = new Date().setUTCHours(0, 0, 0, 0);
        const expirationDate: number = new Date(this.orderRow.get("expiration_date")).setUTCHours(0, 0, 0, 0);
        const expired: boolean = expirationDate < currentDate;
        return QUOTE_STATUS_APPROVED === this.orderRow.get("quote_status") && !expired;
    }

    private isQuoteOrderId(orderId: string): boolean {
        return orderId?.startsWith(QUOTE_ID_PREFIX) ?? false;
    }

    private navigateToOrderTabView(orderId: string, selectCarrier: boolean, shouldOpenInNewTab: boolean = false): void {
        OrderTabView.navigateTo({ key: orderId, newTab: shouldOpenInNewTab, additionalProps: { selectCarrier: selectCarrier }});
    }

    private async orderIdForQuote(quoteId: string): Promise<any> {
        const endpoint = "lme/dispatch/brltl/get-created-order-id-for-quote";
        const result = await Api.post(endpoint, { quote_id: quoteId });
        return result?.data[0];
    }

    private async convertQuoteToOrder(orderId: string): Promise<any> {
        const endpoint = "lme/dispatch/convert-quote-to-order";
        const result = await Api.search(endpoint, { quote_id: orderId, delay_posting: false});
        return result?.data[0];
    }

    configureSelectColumn(): boolean {
        const column: TableColumn = this.tableBFGs.columns.find(col => {
            if ("columnHeaderSelected" === col?.headingCell?.id || "columnHeaderSelected" === col?.id) {
                return true;
            }
            return this.hasComponentId(col?.cellDef?.def, "checkboxInclude");
        });
        const headingCell: TableCell = column?.headingCell;
        if (headingCell == null) {
            return false;
        }

        this.checkboxSelectAll = new Checkbox({
            id: "checkboxSelectAll",
            align: HorizontalAlignment.LEFT,
            color: "primary",
            checkSize: 28,
            padding: 0,
            onClick: (event) => this.checkboxSelectAllOnClick(event)
        });
        this.checkboxSelectAll.allowIndeterminate = true;
        this.checkboxSelectAll.enabled = true;
        headingCell.components = [];
        headingCell.add(this.checkboxSelectAll);
        return true;
    }

    private hasComponentId(definition: any, componentId: string): boolean {
        if (definition == null) {
            return false;
        }
        if (definition.id === componentId) {
            return true;
        }
        if (Array.isArray(definition.components)) {
            return definition.components.some((child: any) => this.hasComponentId(child, componentId));
        }
        return false;
    }

    checkboxSelectAllOnClick(event: ClickEvent) {
        this.tableBFGs.allRows.forEach(tableRow => {
            const modelRow = tableRow.data as ModelRow;
            const checkboxInclude: Checkbox = tableRow.findComponentById("checkboxInclude") as Checkbox;
            if (checkboxInclude) {
                checkboxInclude.checked = !this.allUnchecked ? false : true;
            }
            modelRow.set("bfg_status_code", !this.allUnchecked ? BfgStatusCode.Exclude : BfgStatusCode.Include);
            modelRow.post();
        });
        this.syncSelectAllCheckbox();
    }

    async syncSelectAllCheckbox() {
        if (this.checkboxSelectAll == null) {
            return;
        }

        const disableSelection = this.tableBFGs.allRows.length === 0 || this.selectedForDispatchCarrier;
        this.checkboxSelectAll.enabled = !disableSelection;
        this.checkboxSelectAll.enabled = !disableSelection;

        if (disableSelection) {
            await this.disableRowCheckboxes();
            if (!this.selectedForDispatchCarrier) {
                this.allUnchecked = true;
                return;
            }
        }

        const rows = this.tableBFGs.rows;
        const allSelected = rows.every(row => this.isIncludedCarrierQuote(row.data));
        const noneSelected = rows.every(row => !this.isIncludedCarrierQuote(row.data));

        if (allSelected) {
            this.checkboxSelectAll.checked = true;
            this.allUnchecked = false;
        } else if (noneSelected) {
            this.checkboxSelectAll.checked = false;
            this.allUnchecked = true;
        } else {
            this.checkboxSelectAll.checked = undefined;
            this.allUnchecked = false;
        }

        this.checkboxSelectAll.tooltip = this.allUnchecked ? "Select all" : "Unselect all";
    }

    private async disableRowCheckboxes(): Promise<void> {
        this.tableBFGs.ensureLoaded();

        for (const row of this.tableBFGs.allRows) {
            await row.populateDOM();

            const checkbox = row.findComponentById("checkboxInclude") as Checkbox;
            if (checkbox != null) {
                checkbox.enabled = false;
            }
        }
    }

    checkboxIncludeOnClick(event: ClickEvent) {
        const checkbox: Checkbox = event.target as Checkbox;
        const tableRow: TableRow = TableRow.getContainingTableRow(checkbox);
        const modelRow: ModelRow = tableRow.data;
        modelRow.set("bfg_status_code", checkbox.checked ? BfgStatusCode.Include : BfgStatusCode.Exclude);
        modelRow.post();
        this.syncSelectAllCheckbox();
    }

}
